import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { ReactNode } from "react";
import { SafeDialog } from "./Dialog";

export type ResourceInfo = {
  resourceType: string | null | undefined;
  resourceName: string | number | null | undefined;
};

export type ResourceInfoDisplayProps = {
  resourceType: string | null | undefined;
  resourceName: string | number | null | undefined;
  labelSize?: number;
  valueSize?: number;
};

/**
 * Displays a single resource with label/value in a grid format.
 * Exported for use in custom ConfirmDialog content compositions.
 */
export function ResourceInfoDisplay({
  resourceType,
  resourceName,
  labelSize = 2.5,
  valueSize = 9.5,
}: ResourceInfoDisplayProps) {
  return resourceType || resourceName ? (
    <>
      <Grid size={labelSize}>
        <DialogContentText
          sx={{
            fontSize: ".75rem",
          }}
        >
          {resourceType}
        </DialogContentText>
      </Grid>
      <Grid size={valueSize}>{resourceName}</Grid>
    </>
  ) : null;
}

/**
 * Container component for displaying a list of resources.
 * Exported for use in custom ConfirmDialog content compositions.
 */
export function ResourceList({
  resources,
  spacing = 2,
}: {
  resources: ResourceInfo[];
  spacing?: number;
}) {
  return (
    <Grid container spacing={spacing} paddingBottom={3} alignItems="center">
      {resources.map((resource) => (
        <ResourceInfoDisplay
          key={resource.resourceType}
          resourceType={resource.resourceType}
          resourceName={resource.resourceName}
        />
      ))}
    </Grid>
  );
}

type ConfirmDialogProps = {
  open: boolean;
  loading?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
  header: string;
  resources?: ResourceInfo[];
  preResourcesInfo?: string | ReactNode;
  info?: string | ReactNode;
  confirmText?: string | ReactNode;
  cancelText?: string | ReactNode;
  actions?: ReactNode;
  disableConfirm?: boolean;
  children?: ReactNode;
  confirmStartIcon?: ReactNode;
  cancelStartIcon?: ReactNode;
  minWidth?: string;
  maxWidth?: string;
};

export function ConfirmDialog(props: ConfirmDialogProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      props.onConfirm?.();
    }
  };

  const handleCancel = props.onCancel ?? props.onClose;

  return (
    <SafeDialog
      open={props.open}
      onKeyDown={handleKeyDown}
      onClose={props.onClose}
      slotProps={{
        paper: {
          sx: {
            minWidth: props.minWidth ?? "440px",
            maxWidth: props.maxWidth ?? "640px",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {props.header}
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          disabled={props.loading}
          sx={{
            alignSelf: "start",
            marginLeft: 2,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {props.children ? (
          props.children
        ) : (
          <>
            {props.preResourcesInfo && (
              <DialogContentText>{props.preResourcesInfo}</DialogContentText>
            )}
            {props?.resources && <ResourceList resources={props.resources} />}
            {props.info && <DialogContentText>{props.info}</DialogContentText>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {props.actions ? (
          props.actions
        ) : (
          <>
            <Button
              loading={props.loading}
              disabled={props.disableConfirm}
              onClick={props.onConfirm}
              data-testid="confirm"
              startIcon={props.confirmStartIcon}
            >
              {props.loading
                ? "Loading..."
                : props.confirmText ?? "Yes, Delete"}
            </Button>
            <Button disabled={props.loading} onClick={handleCancel}>
              {props.cancelText ?? "Cancel"}
            </Button>
          </>
        )}
      </DialogActions>
    </SafeDialog>
  );
}
